import { convertMeasure, getInputFieldTypes } from '@e2m-customer-portal/shared';
import { endOfMonth, getTime, isAfter, isBefore, isFuture } from 'date-fns';
import { DeviceWithInstalledPower } from 'domains/forecast/control-energy-forecast/ControlEnergyForecast';
import { t, TFunction } from 'i18next';
import { Dispatch, SetStateAction } from 'react';
import { ForecastSlice, InputFieldType } from '../../../../generated/graphql';
import { chartSeriesColors } from './LineChart';

export const formatTooltipLineChart = (
  t: TFunction,
  locale: string,
  marketLocationName: string,
  deviceName: string,
  intervalEnd: number,
  latestHistoricalSpotPrice?: number,
) =>
  function (this: Highcharts.TooltipFormatterContextObject) {
    // Hide the tooltip for the last timestamp, as most of the data here is faked to make the graph prettier
    if (this.x === intervalEnd) return '';

    const date = new Date(this.x as number);
    return `<div class="w-56 text-wrap bg-white p-4 shadow-md">
      <p class="my-0 text-xs leading-normal font-semibold text-grey-300">${marketLocationName}</p>
      <h3 class="my-1">${deviceName}</h3>
      <h4>${t('MARKET_LOCATION_FEED_IN_POPUP_DATE', { date })}</h4>
      ${this.points?.map((point) => getTooltipValue(point, locale, 'kW', latestHistoricalSpotPrice)).join('')}
      ${
        this.points?.some(
          (point) =>
            point.series.name === t('SPOT_PRICES') &&
            point.x &&
            ((latestHistoricalSpotPrice && isAfter(getTime(point.x), latestHistoricalSpotPrice)) ||
              !latestHistoricalSpotPrice),
        ) ||
        (this.points?.some((point) => point.series.name === t('SPOT_PRICES_MONTHLY_MARKET_VALUE', { month: null })) &&
          (isFuture(endOfMonth(date)) || !latestHistoricalSpotPrice))
          ? `<p class="text-[10px] before:[content:'*_'] -indent-2 pl-2">${t('MARKET_LOCATION_FORECAST_LINE_CHART_SPOT_PRICES_DISCLAIMER')}</p>`
          : ''
      }
    </div>`;
  };

export const getPowerFeedData = (slices: readonly ForecastSlice[], intervalEnd: number, unit = 'kW') => {
  const data = slices.map((slice) => {
    const powerFeedField = slice.fields.find((field) => field.type === InputFieldType.POWER_FEED);

    return {
      x: getTime(slice.date),
      y:
        powerFeedField?.watt != null
          ? convertMeasure(
              {
                value: powerFeedField.watt,
                unit: 'W',
              },
              unit,
            ).value
          : undefined,
    };
  });

  return [...data, { ...data.at(-1), x: intervalEnd }];
};

export const getProductionData = (slices: readonly ForecastSlice[], intervalEnd: number, unit = 'kW') => {
  const data = slices.map((slice) => {
    const productionField = slice.fields.find((field) => field.type === InputFieldType.PRODUCTION);

    return {
      x: getTime(slice.date),
      y:
        productionField?.watt != null
          ? convertMeasure(
              {
                value: productionField.watt,
                unit: 'W',
              },
              unit,
            ).value
          : undefined,
    };
  });

  return [...data, { ...data.at(-1), x: intervalEnd }];
};

export const getIntradayAdjustmentData = (slices: readonly ForecastSlice[], intervalEnd: number, unit = 'kW') => {
  const data = slices
    .filter((slice) => isBefore(slice.date, intervalEnd))
    .map((slice) => {
      const committedProductionField = slice.fields.find(
        (field) => field.type === InputFieldType.DAY_AHEAD_COMMITTED_PRODUCTION,
      );
      const productionField = slice.fields.find((field) => field.type === InputFieldType.PRODUCTION);

      return {
        x: getTime(slice.date),
        y:
          committedProductionField?.watt != null && productionField?.watt != null
            ? convertMeasure(
                {
                  value: productionField.watt - committedProductionField.watt,
                  unit: 'W',
                },
                unit,
              ).value
            : undefined,
        low:
          committedProductionField?.watt != null && productionField?.watt != null
            ? convertMeasure(
                {
                  value: Math.min(committedProductionField.watt, productionField.watt),
                  unit: 'W',
                },
                unit,
              ).value
            : undefined,
        high:
          committedProductionField?.watt != null && productionField?.watt != null
            ? convertMeasure(
                {
                  value: Math.max(committedProductionField.watt, productionField.watt),
                  unit: 'W',
                },
                unit,
              ).value
            : undefined,
      };
    });

  return [...data, { ...data.at(-1), x: intervalEnd }];
};

export const getDataAfrrPlus = (
  bandForecasts: readonly ForecastSlice[],
  slices: ForecastSlice[],
  intervalEnd: number,
  unit = 'kW',
  battery?: boolean,
) => {
  const data = bandForecasts.map((slice) => {
    const afrrPlusField = slice?.fields?.find((field) => field.type === InputFieldType.AFRR_POSITIVE);
    const productionField = battery
      ? slices
          ?.find((prodSlice) => getTime(prodSlice.date) === getTime(slice.date))
          ?.fields.find((field) => field.type === InputFieldType.POWER_FEED) || {
          type: InputFieldType.POWER_FEED,
          value: 0,
          watt: 0,
        }
      : slices
          ?.find((prodSlice) => getTime(prodSlice.date) === getTime(slice.date))
          ?.fields.find((field) => field.type === InputFieldType.PRODUCTION);

    return {
      x: getTime(slice.date),
      low:
        productionField?.watt != null
          ? convertMeasure(
              {
                value: productionField.watt,
                unit: 'W',
              },
              unit,
            ).value
          : undefined,
      high:
        afrrPlusField?.watt != null && productionField?.watt != null
          ? convertMeasure(
              {
                value: productionField.watt + afrrPlusField.watt,
                unit: 'W',
              },
              unit,
            ).value
          : undefined,
    };
  });

  return [...data, { ...data.at(-1), x: intervalEnd }];
};

export const getDataAfrrMinus = (
  bandForecasts: readonly ForecastSlice[],
  slices: ForecastSlice[],
  intervalEnd: number,
  unit = 'kW',
  battery?: boolean,
) => {
  const data = bandForecasts.map((slice) => {
    const afrrMinusField = slice?.fields?.find((field) => field.type === InputFieldType.AFRR_NEGATIVE);
    const productionField = battery
      ? slices
          ?.find((prodSlice) => getTime(prodSlice.date) === getTime(slice.date))
          ?.fields.find((field) => field.type === InputFieldType.POWER_FEED) || {
          type: InputFieldType.POWER_FEED,
          value: 0,
          watt: 0,
        }
      : slices
          ?.find((prodSlice) => getTime(prodSlice.date) === getTime(slice.date))
          ?.fields.find((field) => field.type === InputFieldType.PRODUCTION);

    return {
      x: getTime(slice.date),
      low:
        afrrMinusField?.watt != null && productionField?.watt != null
          ? convertMeasure(
              {
                value: productionField.watt - afrrMinusField.watt,
                unit: 'W',
              },
              unit,
            ).value
          : undefined,
      high:
        productionField?.watt != null
          ? convertMeasure(
              {
                value: productionField.watt,
                unit: 'W',
              },
              unit,
            ).value
          : undefined,
    };
  });

  return [...data, { ...data.at(-1), x: intervalEnd }];
};

export const getDataMfrrPlus = (
  bandForecasts: readonly ForecastSlice[],
  slices: ForecastSlice[],
  intervalEnd: number,
  unit = 'kW',
) => {
  const data = bandForecasts.map((slice) => {
    const mfrrPlusField = slice?.fields?.find((field) => field.type === InputFieldType.MFRR_POSITIVE);
    const productionField = slices
      ?.find((prodSlice) => getTime(prodSlice.date) === getTime(slice.date))
      ?.fields.find((field) => field.type === InputFieldType.PRODUCTION);

    return {
      x: getTime(slice.date),
      low:
        productionField?.watt != null
          ? convertMeasure(
              {
                value: productionField.watt,
                unit: 'W',
              },
              unit,
            ).value
          : undefined,
      high:
        mfrrPlusField?.watt != null && productionField?.watt != null
          ? convertMeasure(
              {
                value: productionField.watt + mfrrPlusField.watt,
                unit: 'W',
              },
              unit,
            ).value
          : undefined,
    };
  });

  return [...data, { ...data.at(-1), x: intervalEnd }];
};

export const getDataMfrrMinus = (
  bandForecasts: readonly ForecastSlice[],
  slices: ForecastSlice[],
  intervalEnd: number,
  unit = 'kW',
) => {
  const data = bandForecasts.map((slice) => {
    const mfrrMinusField = slice?.fields?.find((field) => field.type === InputFieldType.MFRR_NEGATIVE);
    const productionField = slices
      ?.find((prodSlice) => getTime(prodSlice.date) === getTime(slice.date))
      ?.fields.find((field) => field.type === InputFieldType.PRODUCTION);

    return {
      x: getTime(slice.date),
      low:
        mfrrMinusField?.watt != null && productionField?.watt != null
          ? convertMeasure(
              {
                value: productionField.watt - mfrrMinusField.watt,
                unit: 'W',
              },
              unit,
            ).value
          : undefined,
      high:
        productionField?.watt != null
          ? convertMeasure(
              {
                value: productionField.watt,
                unit: 'W',
              },
              unit,
            ).value
          : undefined,
    };
  });

  return [...data, { ...data.at(-1), x: intervalEnd }];
};

export const getDataFcr = (
  bandForecasts: readonly ForecastSlice[],
  slices: ForecastSlice[],
  intervalEnd: number,
  unit = 'kW',
  battery?: boolean,
) => {
  const data = bandForecasts.map((slice) => {
    const fcrField = slice?.fields?.find((field) => field.type === InputFieldType.FCR);
    const productionField = battery
      ? slices
          ?.find((prodSlice) => getTime(prodSlice.date) === getTime(slice.date))
          ?.fields.find((field) => field.type === InputFieldType.POWER_FEED) || {
          type: InputFieldType.POWER_FEED,
          value: 0,
          watt: 0,
        }
      : slices
          ?.find((prodSlice) => getTime(prodSlice.date) === getTime(slice.date))
          ?.fields.find(
            (field) => field.type === InputFieldType.PRODUCTION || field.type === InputFieldType.POWER_FEED,
          );

    return {
      x: getTime(slice.date),
      low:
        fcrField?.watt != null && productionField?.watt != null
          ? convertMeasure(
              {
                value: productionField.watt - fcrField.watt,
                unit: 'W',
              },
              unit,
            ).value
          : undefined,
      high:
        fcrField?.watt != null && productionField?.watt != null
          ? convertMeasure(
              {
                value: productionField.watt + fcrField.watt,
                unit: 'W',
              },
              unit,
            ).value
          : undefined,
    };
  });

  return [...data, { ...data.at(-1), x: intervalEnd }];
};

export const getAreaRangeSeries = (
  controlEnergySlices: readonly ForecastSlice[],
  slices: ForecastSlice[],
  device: DeviceWithInstalledPower,
  visibleSeries: string[],
  intervalEnd: number,
): Highcharts.SeriesOptionsType[] => {
  const bandForecasts = [...controlEnergySlices].sort((a, b) => getTime(a.date) - getTime(b.date));
  const bands: InputFieldType[] = getInputFieldTypes(device.products, 'bands') ?? [];
  const areaRangeSeries: Highcharts.SeriesOptionsType[] = [];
  if (bands.includes(InputFieldType.FCR)) {
    areaRangeSeries.push({
      type: 'arearange',
      id: `fcr-${device.id}`,
      step: 'left',
      stickyTracking: false,
      lineWidth: 0,
      color: chartSeriesColors.fcr,
      data: getDataFcr(bandForecasts, slices, intervalEnd),
      name: t('SHARED_PRODUCT_FCR'),
      marker: { enabled: false },
      visible: visibleSeries.includes(`fcr-${device.id}`),
      states: { hover: { enabled: false } },
    });
  }
  if (bands.includes(InputFieldType.AFRR_POSITIVE)) {
    areaRangeSeries.push({
      type: 'arearange',
      id: `afrrPlus-${device.id}`,
      step: 'left',
      stickyTracking: false,
      lineWidth: 0,
      color: chartSeriesColors.afrrPlus,
      data: getDataAfrrPlus(bandForecasts, slices, intervalEnd),
      name: t('SHARED_PRODUCT_AFRR_POSITIVE'),
      marker: { enabled: false },
      visible: visibleSeries.includes(`afrrPlus-${device.id}`),
      states: { hover: { enabled: false } },
    });
  }
  if (bands.includes(InputFieldType.AFRR_NEGATIVE)) {
    areaRangeSeries.push({
      type: 'arearange',
      id: `afrrMinus-${device.id}`,
      step: 'left',
      stickyTracking: false,
      lineWidth: 0,
      color: chartSeriesColors.afrrMinus,
      data: getDataAfrrMinus(bandForecasts, slices, intervalEnd),
      name: t('SHARED_PRODUCT_AFRR_NEGATIVE'),
      marker: { enabled: false },
      visible: visibleSeries.includes(`afrrMinus-${device.id}`),
      states: { hover: { enabled: false } },
    });
  }
  if (bands.includes(InputFieldType.MFRR_NEGATIVE)) {
    areaRangeSeries.push({
      type: 'arearange',
      id: `mfrrMinus-${device.id}`,
      step: 'left',
      stickyTracking: false,
      lineWidth: 0,
      color: chartSeriesColors.mfrrMinus,
      data: getDataMfrrMinus(bandForecasts, slices, intervalEnd),
      name: t('SHARED_PRODUCT_MFRR_NEGATIVE'),
      marker: { enabled: false },
      visible: visibleSeries.includes(`mfrrMinus-${device.id}`),
      states: { hover: { enabled: false } },
    });
  }
  if (bands.includes(InputFieldType.MFRR_POSITIVE)) {
    areaRangeSeries.push({
      type: 'arearange',
      id: `mfrrPlus-${device.id}`,
      step: 'left',
      stickyTracking: false,
      lineWidth: 0,
      color: chartSeriesColors.mfrrPlus,
      data: getDataMfrrPlus(bandForecasts, slices, intervalEnd),
      name: t('SHARED_PRODUCT_MFRR_POSITIVE'),
      marker: { enabled: false },
      visible: visibleSeries.includes(`mfrrPlus-${device.id}`),
      states: { hover: { enabled: false } },
    });
  }

  return areaRangeSeries;
};

export const getTooltipValue = (
  context: Highcharts.TooltipFormatterContextObject,
  locale: string,
  unit = 'kW',
  latestHistoricalSpotPrice?: number,
) => {
  if (context.point.options.high != null && context.point.options.low != null)
    switch (context.series.name) {
      case t('SHARED_PRODUCT_FCR'):
        return `<p><span class="text-chart-fcr">&#x25AC</span> ${context.series.name}: <span class="font-semibold text-chart-fcr">${Math.round((context.point.options.high - context.point.options.low) / 2).toLocaleString(locale)} ${unit} </span></p>`;
      case t('SHARED_PRODUCT_AFRR_POSITIVE'):
      case t('SHARED_PRODUCT_AFRR_POSITIVE_BATTERY'):
        return `<p><span class="text-chart-afrrPlus">&#x25AC</span> ${context.series.name}: <span class="font-semibold text-chart-afrrPlus">${Math.round(context.point.options.high - context.point.options.low).toLocaleString(locale)} ${unit}</span></p>`;
      case t('SHARED_PRODUCT_AFRR_NEGATIVE'):
      case t('SHARED_PRODUCT_AFRR_NEGATIVE_BATTERY'):
        return `<p><span class="text-chart-afrrMinus">&#x25AC</span> ${context.series.name}: <span class="font-semibold text-chart-afrrMinus">${Math.round(context.point.options.high - context.point.options.low).toLocaleString(locale)} ${unit}</span></p>`;
      case t('SHARED_PRODUCT_MFRR_POSITIVE'):
        return `<p><span class="text-chart-mfrrPlus">&#x25AC</span> ${context.series.name}: <span class="font-semibold text-chart-mfrrPlus">${Math.round(context.point.options.high - context.point.options.low).toLocaleString(locale)} ${unit}</span></p>`;
      case t('SHARED_PRODUCT_MFRR_NEGATIVE'):
        return `<p><span class="text-chart-mfrrMinus">&#x25AC</span> ${context.series.name}: <span class="font-semibold text-chart-mfrrMinus">${Math.round(context.point.options.high - context.point.options.low).toLocaleString(locale)} ${unit}</span></p>`;
    }
  if (context.point.options.y != null)
    switch (context.series.name) {
      case t('SHARED_PRODUCT_PRODUCTION'):
        return `<p><span class="text-chart-production">&#x25AC</span> ${context.series.name}: <span class="font-semibold text-chart-production">${Math.round(context.point.options.y).toLocaleString(locale)} ${unit}</span></p>`;
      case t('SHARED_PRODUCT_POWER_FEED_BATTERY'):
        return `<p><span class="text-chart-powerFeed">&#x25AC</span> ${context.series.name}: <span class="font-semibold text-chart-powerFeed">${Math.round(context.point.options.y).toLocaleString(locale)} ${unit}</span></p>`;
      case t('SHARED_PRODUCT_INTRADAY_ADJUSTMENT_PLUS'):
      case t('SHARED_PRODUCT_INTRADAY_ADJUSTMENT_MINUS'):
        if (!context.point.options.y) return '';
        else {
          return `<p><span class="${context.point.options.y < 0 ? 'text-chart-intraday relative inline-block rotate-45' : 'text-chart-intraday'}">&#x25AC</span> ${context.series.name}: <span class="font-semibold text-chart-intraday">${Math.round(context.point.options.y).toLocaleString(locale)} ${unit}</span></p>`;
        }
      case t('SPOT_PRICES'):
        return `<p><span class="text-chart-spotPrices">&#x25AC</span> ${context.series.name}: <span class="font-semibold text-chart-spotPrices">${context.point.options.y < 0 ? '-' : ''}${Math.abs(context.point.options.y).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/MWh${!latestHistoricalSpotPrice || isAfter(getTime(context.point.x), latestHistoricalSpotPrice) ? '*' : ''}  </span></p>`;
      case t('SPOT_PRICES_MONTHLY_MARKET_VALUE', { month: null }):
        return `<p><span class="text-chart-monthlyMarketValue">&#x25AC</span> ${context.series.name}: <span class="font-semibold text-chart-monthlyMarketValue">${context.point.options.y < 0 ? '-' : ''}${Math.abs(context.point.options.y).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/MWh${isFuture(endOfMonth(context.point.x)) ? '*' : ''}</span></p>`;
    }

  return '';
};

export const legendItemClick =
  (setVisibleSeries: Dispatch<SetStateAction<string[]>>): Highcharts.SeriesLegendItemClickCallbackFunction =>
  (event: Highcharts.SeriesLegendItemClickEventObject) => {
    setVisibleSeries((visibleSeries) => {
      if (event.target.options.id && !visibleSeries.includes(event.target.options.id))
        return [...visibleSeries, event.target.options.id];
      else return visibleSeries.filter((series) => series !== event.target.options.id);
    });
  };
